import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  AddOrgMembersPayload,
  AddOrgMembersResponse,
  AdminEnvelope,
  AdminOrgInviteLink,
  AdminOrgInvitesResponse,
  AdminOrgMember,
  AdminOrgMembersResponse,
  AdminOrgRequestsResponse,
  InviteNonFFUserPayload,
} from '@/client/types/admin/users-orgs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useGetMutationFn from '../use-get-mutation-fn';
import useGetQueryFn from '../use-get-query-fn';

export const adminOrgMemberKeys = {
  all: ['admin', 'org-members'] as const,
  members: (oid: string | number | null) =>
    [...adminOrgMemberKeys.all, 'members', oid] as const,
  requests: (oid: string | number | null) =>
    [...adminOrgMemberKeys.all, 'requests', oid] as const,
  invites: (oid: string | number | null, filters: Record<string, unknown>) =>
    [...adminOrgMemberKeys.all, 'invites', oid, filters] as const,
  inviteLink: (oid: string | number | null) =>
    [...adminOrgMemberKeys.all, 'invite-link', oid] as const,
  roleCatalog: () => [...adminOrgMemberKeys.all, 'role-catalog'] as const,
};

/* ────────────────────────────── Roster ────────────────────────────── */

/**
 * `GET /api/admin/organizations/<oid>/members`
 *
 * Returns the ENTIRE roster — it accepts no pagination params despite what the
 * backend docstring claims (gap M-6). Paginate client-side for large orgs.
 * The owner is included as a de-facto member.
 */
export const useOrgMembersQuery = (oid: string | number | null, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrgMembersResponse>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching members',
    endpoint: `/api/admin/organizations/${oid}/members`,
  });

  return useQuery({
    queryKey: adminOrgMemberKeys.members(oid),
    queryFn: () => queryFn({}),
    enabled: Boolean(oid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/**
 * `POST /api/admin/organizations/<oid>/members` — add existing FF user(s).
 *
 * `uid` accepts a scalar, CSV string, or array; a single `role` applies to all
 * of them. Partial success is normal and returns **207** with both `added` and
 * `errors` populated — callers must report per-uid failures rather than
 * treating a 2xx as total success.
 */
export const useAddOrgMembersMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    AddOrgMembersPayload,
    AdminEnvelope<AddOrgMembersResponse>
  >({
    defaultErrorMessage: 'An unexpected error occurred while adding members',
    endpoint: (payload) => `/api/admin/organizations/${payload.oid}/members`,
    method: 'POST',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminOrgMemberKeys.members(variables.oid),
      });
    },
  });
};

export type OrgMemberAction =
  | 'activate'
  | 'deactivate'
  | 'delete'
  | 'undelete'
  | 'onboard';

export const useOrgMemberLifecycleMutation = () => {
  const queryClient = useQueryClient();

  const post = useGetMutationFn<
    { oid: string | number; uid: number; action: OrgMemberAction },
    AdminEnvelope<AdminOrgMember>
  >({
    defaultErrorMessage: 'An unexpected error occurred while updating the member',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/members/${payload.uid}/${payload.action}`,
    method: 'POST',
  });

  const remove = useGetMutationFn<
    { oid: string | number; uid: number },
    AdminEnvelope<AdminOrgMember>
  >({
    defaultErrorMessage: 'An unexpected error occurred while removing the member',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/members/${payload.uid}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn: ({
      oid,
      uid,
      action,
    }: {
      oid: string | number;
      uid: number;
      action: OrgMemberAction;
    }) => (action === 'delete' ? remove({ oid, uid }) : post({ oid, uid, action })),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminOrgMemberKeys.members(variables.oid),
      });
    },
  });
};


/** Slim picker feed — `permissions=0` skips the grant join. */
export { useOrgRoleCatalogQuery } from './organizations.query';

export const useOrgMemberRoleMutation = () => {
  const queryClient = useQueryClient();

  const assign = useGetMutationFn<
    { oid: string | number; uid: number; ourid: number },
    AdminEnvelope<unknown>
  >({
    defaultErrorMessage: 'An unexpected error occurred while assigning the role',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/members/${payload.uid}/role/${payload.ourid}`,
    method: 'POST',
  });

  const unassign = useGetMutationFn<
    { oid: string | number; uid: number; ourid: number },
    AdminEnvelope<{ success: boolean }>
  >({
    defaultErrorMessage: 'An unexpected error occurred while removing the role',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/members/${payload.uid}/role/${payload.ourid}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn: ({
      oid,
      uid,
      ourid,
      assigned,
    }: {
      oid: string | number;
      uid: number;
      ourid: number;
      assigned: boolean;
    }) => (assigned ? assign({ oid, uid, ourid }) : unassign({ oid, uid, ourid })),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminOrgMemberKeys.members(variables.oid),
      });
    },
  });
};

/* ────────────────────────── Invites & requests ────────────────────────── */

/** `GET /api/admin/organizations/<oid>/requests` — pending FF-user requests. */
export const useOrgRequestsQuery = (oid: string | number | null, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrgRequestsResponse>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching requests',
    endpoint: `/api/admin/organizations/${oid}/requests`,
  });

  return useQuery({
    queryKey: adminOrgMemberKeys.requests(oid),
    queryFn: () => queryFn({}),
    enabled: Boolean(oid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/** `GET /api/admin/organizations/<oid>/invites` — pending non-FF invites. */
export const useOrgInvitesQuery = (
  oid: string | number | null,
  filters: { email?: string; phone?: string; hash?: string } = {},
  enabled = true
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrgInvitesResponse>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching invites',
    endpoint: `/api/admin/organizations/${oid}/invites`,
  });

  return useQuery({
    queryKey: adminOrgMemberKeys.invites(oid, filters),
    queryFn: () => queryFn({ ...filters }),
    enabled: Boolean(oid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/**
 * `POST /api/admin/organizations/<oid>/invites` — invite a non-FF user.
 *
 * `role` is required, plus AT LEAST ONE of `email` / `phone` (both may be sent;
 * CSV is accepted). Validate that before submitting — the backend returns a
 * bare 400 that doesn't explain which half is missing.
 */
export const useInviteNonFFUserMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<InviteNonFFUserPayload, AdminEnvelope<unknown>>({
    defaultErrorMessage: 'An unexpected error occurred while sending the invite',
    endpoint: (payload) => `/api/admin/organizations/${payload.oid}/invites`,
    method: 'POST',
  });

  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminOrgMemberKeys.all,
      });
      void variables;
    },
  });
};

/**
 * `DELETE /api/admin/organizations/<oid>/invites/<ident>` — cancel a pending item.
 *
 * `ident` is an email, phone, or hash. Note this same route cancels pending
 * FF-user REQUESTS too — there is no `/requests/<ident>` DELETE (gap M-6).
 */
export const useCancelOrgInviteMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { oid: string | number; ident: string },
    AdminEnvelope<unknown>
  >({
    defaultErrorMessage: 'An unexpected error occurred while cancelling the invite',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/invites/${encodeURIComponent(payload.ident)}`,
    method: 'DELETE',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminOrgMemberKeys.all });
    },
  });
};

/** `PUT .../invites/<ident>/role/<role>` — change a pending invite's role. */
export const useUpdateInviteRoleMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = useGetMutationFn<
    { oid: string | number; ident: string; role: string },
    AdminEnvelope<unknown>
  >({
    defaultErrorMessage: 'An unexpected error occurred while updating the invite role',
    endpoint: (payload) =>
      `/api/admin/organizations/${payload.oid}/invites/${encodeURIComponent(payload.ident)}/role/${payload.role}`,
    method: 'PUT',
  });

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminOrgMemberKeys.all });
    },
  });
};

/** `GET /api/admin/organizations/<oid>/invite-link`. */
export const useOrgInviteLinkQuery = (
  oid: string | number | null,
  enabled = true
) => {
  const queryFn = useGetQueryFn<AdminEnvelope<AdminOrgInviteLink>>({
    defaultErrorMessage: 'An unexpected error occurred while fetching the invite link',
    endpoint: `/api/admin/organizations/${oid}/invite-link`,
  });

  return useQuery({
    queryKey: adminOrgMemberKeys.inviteLink(oid),
    queryFn: () => queryFn({}),
    enabled: Boolean(oid) && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};
